import { mdiCalendarClock, mdiCalendarPlus, mdiFileExcel, mdiMapMarkerRadius, mdiPencil, mdiReload } from "@mdi/js";
import { Link } from "easy-page-router/react";
import { useSSRHook } from "ssr-hook";
import { range } from "lodash";
import Icon from "@mdi/react";

import { getGoogleCalendarUrl, useHead, validateEmail } from "../services/common";
import Footer from "../components/Footer";
import { Header, Input, Loading } from "../components";
import Content from "../components/Content";
import { useState } from "react";
import moment from "moment";
import { api, useApi } from "../services/api";
import { useUser } from "../User";
import { EditEducation, EducationDate } from "./Education";
import { Application, Education } from "../../types";


const emptyApplication: Application = {
    _id: "",
    educationId: "",
    date: moment().format(),
    title: "",
    name: "",
    surname: "",
    birthDate: "1990-01-01",
    birthPlace: "",
    email: "",
    phone: "",
    isPrivatePerson: true,
    address: "",
    organizationName: "",
    organizationID: "",
    note: "",
};

const sendingMessage = "Registrace se odesílá...";

export default function ApplicationPage({ id }: { id: string }) {
    const user = useUser();

    const [educations, educationsError, educationsIsLoading, educationsReload] = useSSRHook<Education[]>(`/api/educations`);
    const [education, error, isLoading, reload] = useSSRHook<Education>(`/api/education?educationId=${encodeURIComponent(id)}`);

    const title = education?.title || "Vzdělávání";
    useHead(title, `${title} | Jilemnicko - společenství obcí`);

    const [edit, setEdit] = useState<null | Education>(null);

    const [application, setApplication] = useState<Application>(emptyApplication);
    const [birthDate, setBirthDate] = useState(["1990", "01", "01"]);

    const [message, setMessage] = useState("");

    async function handleApplication() {
        if (!education) return;

        if (!application.name || !application.surname) {
            setMessage("Musíte vyplnit jméno a příjmení.");
            return;
        }
        if (!moment(birthDate.join("-")).isValid()) {
            setMessage("Datum narození je neplatné.");
            return;
        }
        if (!application.birthPlace) {
            setMessage("Musíte vyplnit místo narození.");
            return;
        }
        if (!validateEmail(application.email)) {
            setMessage("E-mail je neplatný.");
            return;
        }
        if (application.isPrivatePerson && !application.address) {
            setMessage("Musíte vyplnit adresu trvalého bydliště.");
            return;
        }
        if (!application.isPrivatePerson && (!application.organizationName || !application.organizationID)) {
            setMessage("Musíte vyplnit informace o organizaci.");
            return;
        }

        setMessage(sendingMessage);
        const [, error] = await api.addApplication({ ...application, birthDate: birthDate.join("-"), educationId: education._id });

        if (error) {
            setMessage("Registrace se nepodařila odeslat. Zkuste to prosím později.");
            return;
        }

        setMessage("Registrace byla úspěšně odeslána.");
        setApplication(emptyApplication);
    }

    return <>
        <Header title={title} background="vzdelavani-2" />
        <main>
            <div className="content-page">
                <div className="content-split application">
                    <div className="left">
                        {user.isAdmin && <div style={{ textAlign: "right" }}>
                            <button onClick={() => setEdit(education)}>
                                <Icon path={mdiPencil} size={1} /> Upravit detaily kurzu
                            </button>

                            <EditEducation
                                education={edit}
                                onChange={() => {
                                    setEdit(null)
                                    reload();
                                }}
                                onClose={() => setEdit(null)}
                            />
                        </div>}

                        {isLoading && <Loading />}
                        {error && <p className="alert">Kurz se bohužel nepodařilo načíst 🙁</p>}
                        {education && <section>
                            <div className="education-date-place">
                                <div className="education-date">
                                    <div className="title">
                                        <div className="icon">
                                            <Icon path={mdiCalendarClock} size={1.7} />
                                        </div>
                                        Datum a čas
                                    </div>
                                    <div className="text"><EducationDate {...education} /></div>
                                </div>
                                <div className="education-place">
                                    <div className="title">
                                        <div className="icon">
                                            <Icon path={mdiMapMarkerRadius} size={2} />
                                        </div>
                                        Místo kurzu
                                    </div>
                                    <div className="text">{education.place}</div>
                                </div>
                            </div>
                            <div style={{ marginBottom: "25px" }}>
                                <a
                                    className="button small"
                                    href={getGoogleCalendarUrl(education)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    <Icon path={mdiCalendarPlus} size={0.8} /> Přidat do Google kalendáře
                                </a>
                            </div>
                            <h1 style={{ marginBottom: "10px" }}>{title}</h1>
                            <div className="education-top" style={{ backgroundImage: `url(${education.picture.url})` }}>
                                <div className="date"><EducationDate {...education} /></div>
                            </div>
                            <Content id={education.contentId} />
                        </section>}

                        {education && <section className="application">
                            <h2>Registrace do kurzu</h2>

                            <Input label="Titul (nepovinné)" name="title" value={application} onChange={setApplication} />
                            <Input label="Jméno" name="name" value={application} onChange={setApplication} />
                            <Input label="Příjmení" name="surname" value={application} onChange={setApplication} />
                            <div>
                                <label>Datum narození</label>
                                <br />
                                <select value={birthDate[2]} onChange={(e) => setBirthDate([birthDate[0], birthDate[1], e.target.value])}>
                                    {range(1, 32).map(d => <option value={d.toFixed(0).padStart(2, "0")} key={d}>{d}.</option>)}
                                </select>
                                <select value={birthDate[1]} onChange={(e) => setBirthDate([birthDate[0], e.target.value, birthDate[2]])}>
                                    {range(1, 13).map(m => <option value={m.toFixed(0).padStart(2, "0")} key={m}>{moment().set("month", m - 1).format("MMMM")}</option>)}
                                </select>
                                <select value={birthDate[0]} onChange={(e) => setBirthDate([e.target.value, birthDate[1], birthDate[2]])}>
                                    {range(1900, Number(moment().format("YYYY")) + 1).map(y => <option value={y} key={y}>{y}</option>)}
                                </select>
                            </div>
                            <Input label="Místo narození" name="birthPlace" value={application} onChange={setApplication} />
                            <Input label="E-mail" name="email" value={application} onChange={setApplication} />
                            <Input label="Telefon (nepovinné)" name="phone" value={application} onChange={setApplication} />
                            <label>
                                <input type="checkbox" checked={application.isPrivatePerson} onChange={(e) => setApplication({ ...application, isPrivatePerson: e.target.checked })} />
                                Vyplňuji za soukromou osobu
                            </label>
                            <div className="split">
                                <div className={application.isPrivatePerson ? "active" : ""}>
                                    <Input textarea label="Adresa trvalého bydliště" name="address" value={application} onChange={setApplication} />
                                </div>
                                <div className={application.isPrivatePerson ? "" : "active"}>
                                    <Input label="Název zastupující organizace" name="organizationName" value={application} onChange={setApplication} />
                                    <Input label="IČ zastupující organizace" name="organizationID" value={application} onChange={setApplication} />
                                </div>
                            </div>

                            <Input textarea label="Poznámka" name="note" value={application} onChange={setApplication} />

                            <p>Potvrzením závazné registrace na kurz uděluji souhlas se zpracováním mé e-mailové adresy za účelem zasílání informací o plánovaných vzdělávacích kurzech a dalších souvisejících akcích Jilemnicka - společenství obcí. Tento souhlas mohu kdykoliv odvolat zasláním žádosti na e-mailovou adresu <a href="mailto:ouhrabkova@jilemnicko-so.cz">ouhrabkova@jilemnicko-so.cz</a>.</p>

                            {message && <p className="alert">{message}</p>}
                            {message !== sendingMessage && <button className="center" onClick={handleApplication}>Potvrzuji závaznou registraci na kurz</button>}
                            {message === sendingMessage && <Loading />}
                        </section>}
                    </div>
                    <div className="right">
                        {educationsIsLoading && <Loading />}
                        {educationsError && <p className="alert">Seznam kurzů se bohužel nepodařilo načíst 🙁</p>}
                        {educations && <div className="educations-list">
                            <h3>Nabízené kurzy</h3>
                            {educations
                                .filter(e => e.active)
                                .sort((a, b) => a.dateFrom.localeCompare(b.dateFrom))
                                .map(e => <Link key={e._id} className="educations-item" to={`/kurz/${e._id}`}>
                                    <img src={e.picture.url} alt={e.title} /> {e.title}
                                </Link>)}
                        </div>}
                    </div>
                </div>
            </div>

            {user.isAdmin && <AdminApplication educationId={id} />}
        </main >
        <Footer />
    </>;
}

function AdminApplication({ educationId }: { educationId: string }) {
    const [result, error, isLoading, status, reload] = useApi.getApplications({ educationId });

    function handleSaveTable(event: any) {
        if (!result) {
            event.preventDefault();
            return;
        }

        function d(s: string): string {
            return `"${s.replace(/;/g, ',').replace(/"/g, '\\"')}"`;
        }

        let csvString = `Datum přihlášky;Titul;Jméno;Příjmení;Datum narození;Místo narození;E-mail;Telefon;Typ;Adresa;Název organizace;IČ organizace;Poznámka
${result.sort((a, b) => b.date.localeCompare(a.date)).map(row => `${moment(row.date).format("DD. MM. YYYY hh:mm:ss")};${d(row.title || "")};${d(row.name)};${d(row.surname)};${d(moment(row.birthDate).format("DD. MM. YYYY"))};${d(row.birthPlace)};${d(row.email)};${d(row.phone || "")};${d(row.isPrivatePerson ? "Soukromá osoba" : "Organizace")};${d(row.address)};${d(row.organizationName)};${d(row.organizationID)};${d(row.note || "")}`).join("\n")}`;

        const bom = new Uint8Array([0xEF, 0xBB, 0xBF]); // BOM pro UTF-8
        const blob = new Blob([bom, csvString], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);

        const a = event.target as HTMLLinkElement;
        a.href = url;
    }

    return <section className="content-page">
        <h2>Administrátorská sekce přihlášek do kurzu</h2>

        <div className="buttons" style={{ display: "inline-flex", gap: "20px", marginBottom: "20px" }}>
            <button onClick={reload}><Icon path={mdiReload} size={1} /> Přenačíst tabulku</button>
            <a className="button" href="#" download="rezervace.csv" onClick={handleSaveTable}><Icon path={mdiFileExcel} size={1} />  Uložit tabulku do souboru</a>
        </div>

        {isLoading && <Loading />}
        {error && <p className="alert">{error}</p>}
        {result && <div className="scroll-x">
            <table className="data-table">
                <thead>
                    <tr>
                        <th></th>
                        <th>Datum přihlášky</th>
                        <th>Titul</th>
                        <th>Jméno</th>
                        <th>Příjmení</th>
                        <th>Datum narození</th>
                        <th>Město narození</th>
                        <th>E-mail</th>
                        <th>Telefon</th>
                        <th>Typ</th>
                        <th>Adresa</th>
                        <th>Název organizace</th>
                        <th>IČ organizace</th>
                        <th>Poznámka</th>
                    </tr>
                </thead>
                <tbody>
                    {result.sort((a, b) => b.date.localeCompare(a.date)).map((r, index) => <tr key={r._id}>
                        <td>{index + 1}</td>
                        <td>{moment(r.date).format("DD. MM. YYYY hh:mm:ss")}</td>
                        <td>{r.title || ""}</td>
                        <td>{r.name}</td>
                        <td>{r.surname}</td>
                        <td>{moment(r.birthDate).format("DD. MM. YYYY")}</td>
                        <td>{r.birthPlace}</td>
                        <td>{r.email}</td>
                        <td>{r.phone || ""}</td>
                        <td>{r.isPrivatePerson ? "Soukromá osoba" : "Organizace"}</td>
                        <td>{r.address}</td>
                        <td>{r.organizationName}</td>
                        <td>{r.organizationID}</td>
                        <td style={{ whiteSpace: "pre-wrap" }}>{r.note || ""}</td>
                    </tr>)}
                </tbody>
            </table>
        </div>}
        {result && result.length === 0 && <p className="info">Zatím zde nejsou žádné rezervace.</p>}
    </section>;
}